import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { hashKioskPin, isKioskPin } from '../../models/kiosk-pin';
import { MembershipService } from '../../services/auth/membership.service';

@Component({
  selector: 'app-kiosk-unlock-dialog',
  template: `
    <h2 mat-dialog-title>Desbloquear</h2>
    <mat-dialog-content>
      <p class="kiosk-unlock-note">Ingresa el PIN del consultorio para volver al menú.</p>
      <mat-form-field appearance="outline" class="kiosk-unlock-field">
        <mat-label>PIN</mat-label>
        <input
          matInput
          type="password"
          inputmode="numeric"
          maxlength="4"
          autocomplete="off"
          [ngModel]="pin()"
          (ngModelChange)="onPin($event)"
          (keydown.enter)="submit()"
        />
      </mat-form-field>
      @if (error()) {
        <p class="kiosk-unlock-error" role="alert">{{ error() }}</p>
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" (click)="close()">Cancelar</button>
      <button mat-flat-button color="primary" type="button" [disabled]="checking()" (click)="submit()">
        {{ checking() ? 'Revisando…' : 'Desbloquear' }}
      </button>
    </mat-dialog-actions>
  `,
  styles: `
    .kiosk-unlock-note { margin: 0 0 12px; }
    .kiosk-unlock-field { display: block; width: 100%; }
    .kiosk-unlock-error { margin: 0; color: var(--mat-sys-error); }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatInputModule],
})
export class KioskUnlockDialogComponent {
  private ref = inject(MatDialogRef<KioskUnlockDialogComponent, boolean>);
  private membership = inject(MembershipService);

  readonly pin = signal('');
  readonly error = signal('');
  readonly checking = signal(false);

  close(): void {
    this.ref.close(false);
  }

  onPin(value: string): void {
    this.pin.set(value.replace(/\D/g, '').slice(0, 4));
    this.error.set('');
  }

  async submit(): Promise<void> {
    if (this.checking()) {
      return;
    }
    const pin = this.pin();
    const expected = this.membership.selectedKioskPinHash();
    if (!expected) {
      this.error.set('No hay un PIN configurado.');
      return;
    }
    if (!isKioskPin(pin)) {
      this.error.set('El PIN debe tener 4 dígitos.');
      return;
    }
    this.checking.set(true);
    try {
      const hash = await hashKioskPin(pin);
      if (hash !== expected) {
        this.error.set('PIN incorrecto.');
        this.pin.set('');
        return;
      }
      this.ref.close(true);
    } finally {
      this.checking.set(false);
    }
  }
}
